// CRUD admin de clases_tipos. Tabla + modal de edición.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, AlertCircle, Loader2, X } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listClasesTipos, createClaseTipo, updateClaseTipo, deleteClaseTipo,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

const NIVELES = ['principiante', 'intermedio', 'avanzado', 'todos'];

const FORM_VACIO = {
  nombre_es: '', nombre_en: '', nombre_ru: '', nombre_fr: '',
  descripcion_es: '', descripcion_en: '', descripcion_ru: '', descripcion_fr: '',
  descripcion_larga_es: '', descripcion_larga_en: '', descripcion_larga_ru: '', descripcion_larga_fr: '',
  nivel: 'todos', duracion_minutos: 60, icono_nombre: 'flower',
};

export default function AdminClasesTipos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [iconos, setIconos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null); // null | {id?, ...form}

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listClasesTipos();
      setItems(r.data || []);
      setIconos(r.iconos_validos || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (item) => {
    if (!window.confirm(t('admin.confirmar_borrar', { nombre: item.nombre_es }))) return;
    try {
      await deleteClaseTipo(item.id);
      cargar();
    } catch (e) {
      alert(e.message || t('admin.error_generico'));
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.clases_tipos.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.clases_tipos.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.clases_tipos.col.nombre')}</th>
              <th>{t('admin.clases_tipos.col.nivel')}</th>
              <th>{t('admin.clases_tipos.col.duracion')}</th>
              <th>{t('admin.clases_tipos.col.icono')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>{c.nombre_es}</td>
                <td><span className="estado-pill">{c.nivel}</span></td>
                <td>{c.duracion_minutos} min</td>
                <td>{c.icono_nombre || '—'}</td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar({ ...FORM_VACIO, ...c })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} strokeWidth={1.5} />
                  </button>
                  <button type="button" onClick={() => onBorrar(c)} aria-label={t('admin.borrar')}>
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <ClaseTipoModal
          item={editar}
          iconos={iconos}
          onCerrar={() => setEditar(null)}
          onGuardado={() => { setEditar(null); cargar(); }}
        />
      )}
    </div>
  );
}

function ClaseTipoModal({ item, iconos, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const editando = !!item.id;

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const payload = {
        nombre_es: form.nombre_es, nombre_en: form.nombre_en,
        nombre_ru: form.nombre_ru || null, nombre_fr: form.nombre_fr || null,
        descripcion_es: form.descripcion_es || null, descripcion_en: form.descripcion_en || null,
        descripcion_ru: form.descripcion_ru || null, descripcion_fr: form.descripcion_fr || null,
        descripcion_larga_es: form.descripcion_larga_es || null,
        descripcion_larga_en: form.descripcion_larga_en || null,
        descripcion_larga_ru: form.descripcion_larga_ru || null,
        descripcion_larga_fr: form.descripcion_larga_fr || null,
        nivel: form.nivel,
        duracion_minutos: Number(form.duracion_minutos),
        icono_nombre: form.icono_nombre || null,
      };
      if (editando) await updateClaseTipo(item.id, payload);
      else await createClaseTipo(payload);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.crear_nuevo')}</h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.clases_tipos.f.nombre_es')}</label>
              <input type="text" value={form.nombre_es} onChange={setCampo('nombre_es')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.clases_tipos.f.nombre_en')}</label>
              <input type="text" value={form.nombre_en} onChange={setCampo('nombre_en')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.clases_tipos.f.nombre_ru')}</label>
              <input type="text" value={form.nombre_ru || ''} onChange={setCampo('nombre_ru')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.clases_tipos.f.nombre_fr')}</label>
              <input type="text" value={form.nombre_fr || ''} onChange={setCampo('nombre_fr')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_es')}</label>
              <textarea rows={2} value={form.descripcion_es || ''} onChange={setCampo('descripcion_es')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_en')}</label>
              <textarea rows={2} value={form.descripcion_en || ''} onChange={setCampo('descripcion_en')} />
            </div>
            {/* mejora-web F4: descripción corta también en RU y FR */}
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_ru')}</label>
              <textarea rows={2} value={form.descripcion_ru || ''} onChange={setCampo('descripcion_ru')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_fr')}</label>
              <textarea rows={2} value={form.descripcion_fr || ''} onChange={setCampo('descripcion_fr')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_larga_es')}</label>
              <textarea rows={5} value={form.descripcion_larga_es || ''} onChange={setCampo('descripcion_larga_es')}
                placeholder={t('admin.clases_tipos.f.descripcion_larga_placeholder')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_larga_en')}</label>
              <textarea rows={5} value={form.descripcion_larga_en || ''} onChange={setCampo('descripcion_larga_en')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_larga_ru')}</label>
              <textarea rows={5} value={form.descripcion_larga_ru || ''} onChange={setCampo('descripcion_larga_ru')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.descripcion_larga_fr')}</label>
              <textarea rows={5} value={form.descripcion_larga_fr || ''} onChange={setCampo('descripcion_larga_fr')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.clases_tipos.f.nivel')}</label>
              <select value={form.nivel} onChange={setCampo('nivel')}>
                {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.clases_tipos.f.duracion')}</label>
              <input type="number" min="1" value={form.duracion_minutos} onChange={setCampo('duracion_minutos')} required />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.clases_tipos.f.icono')}</label>
              <select value={form.icono_nombre || ''} onChange={setCampo('icono_nombre')}>
                <option value="">—</option>
                {iconos.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
